import type { ProductLike, ProductPurposeGroup, PurposeLike } from "../types";

export const productPurposeKey = (
  productUuid: string | undefined,
  purposeUuid: string,
) => (productUuid ? `${productUuid}:${purposeUuid}` : purposeUuid);

export const purposeCoversProduct = (
  purpose: PurposeLike,
  productUuid: string,
): boolean => {
  const scope = purpose.product_uuids;
  if (!scope || scope.length === 0) return true;
  return scope.includes(productUuid);
};

export const isMultiProduct = (products: ProductLike[] | undefined): boolean =>
  Boolean(products && products.length > 1);

export const purposeNamesProduct = (
  purpose: PurposeLike,
  productUuid: string,
): boolean => Boolean(purpose.product_uuids?.includes(productUuid));

export const noticeScopesPurposes = (purposes: PurposeLike[]): boolean =>
  purposes.some((purpose) => (purpose.product_uuids?.length ?? 0) > 0);

export const serverMarksMandatoryProducts = (
  products: ProductLike[] | undefined,
): boolean =>
  Boolean(
    products &&
    products.length > 0 &&
    products.every((product) => product.mandatory !== undefined),
  );

export const gatedProductUuids = (
  products: ProductLike[] | undefined,
): Set<string> =>
  new Set(
    (products ?? [])
      .filter((product) => product.mandatory === true)
      .map((product) => product.uuid),
  );

export const rowProductUuid = (
  row: { productUuid?: string },
  products: ProductLike[] | undefined,
): string | undefined =>
  row.productUuid ?? (products?.length === 1 ? products[0].uuid : undefined);

export const rowsUnderGate = <R extends { productUuid?: string }>(
  rows: R[],
  products: ProductLike[] | undefined,
): R[] => {
  const gated = gatedProductUuids(products);
  if (gated.size === 0) return [];

  return rows.filter((row) => {
    const productUuid = rowProductUuid(row, products);
    return productUuid !== undefined && gated.has(productUuid);
  });
};

export const mandatoryProductsFirst = <P extends ProductLike>(
  products: P[],
): P[] => {
  const mandatory = products.filter((product) => product.mandatory === true);
  if (mandatory.length === 0 || mandatory.length === products.length) {
    return products;
  }
  return [
    ...mandatory,
    ...products.filter((product) => product.mandatory !== true),
  ];
};

/**
 * `purposes` in the admin's saved order: the saved uuids first, then any
 * purpose the order does not name, in their own order. No saved order leaves
 * them as they are.
 */
const inSavedOrder = <U extends PurposeLike>(
  purposes: U[],
  saved: string[] | undefined,
): U[] => {
  if (!saved || saved.length === 0) return purposes;

  const byUuid = new Map(purposes.map((p) => [p.uuid, p]));
  const ordered: U[] = [];
  for (const uuid of saved) {
    const found = byUuid.get(uuid);
    if (found) ordered.push(found);
  }
  const rest = purposes.filter((p) => !saved.includes(p.uuid));
  return [...ordered, ...rest];
};

export const groupPurposesByProduct = <
  P extends ProductLike,
  U extends PurposeLike,
>(
  products: P[] | undefined,
  purposes: U[],
  order?: Record<string, string[]>,
): ProductPurposeGroup<P, U>[] => {
  if (!products || products.length === 0) return [];

  return mandatoryProductsFirst(products)
    .map((product) => {
      const covered = purposes.filter((purpose) =>
        purposeCoversProduct(purpose, product.uuid),
      );

      return { product, purposes: inSavedOrder(covered, order?.[product.uuid]) };
    })
    .filter((group) => group.purposes.length > 0);
};

export const expandPurposesByProduct = <
  P extends ProductLike,
  U extends PurposeLike,
>(
  products: P[] | undefined,
  purposes: U[],
  order?: Record<string, string[]>,
): Array<{ product: P; purpose: U }> => {
  if (!isMultiProduct(products)) return [];

  const rows: Array<{ product: P; purpose: U }> = [];
  for (const group of groupPurposesByProduct(products, purposes, order)) {
    for (const purpose of group.purposes) {
      rows.push({ product: group.product, purpose });
    }
  }
  return rows;
};

export const noticePurposeRows = <P extends ProductLike, U extends PurposeLike>(
  products: P[] | undefined,
  purposes: U[],
  order?: Record<string, string[]>,
): Array<{ purpose: U; productUuid?: string }> => {
  if (!isMultiProduct(products)) {
    // A single-product notice renders ungrouped, but in the order the admin
    // saved for that product, as a grouped notice does per product.
    const sole = products?.[0];
    return inSavedOrder(purposes, sole ? order?.[sole.uuid] : undefined).map(
      (purpose) => ({ purpose, productUuid: undefined }),
    );
  }

  return expandPurposesByProduct(products, purposes, order).map((row) => ({
    purpose: row.purpose,
    productUuid: row.product.uuid,
  }));
};

/**
 * Each purpose once, in the order the notice renders them: the saved order of
 * a single-product notice, or product by product on a grouped one. For
 * everything that walks purposes rather than rows -- the reconsent split and
 * talkback -- so it follows what the visitor sees.
 */
export const purposesInRenderOrder = <
  P extends ProductLike,
  U extends PurposeLike,
>(
  products: P[] | undefined,
  purposes: U[],
  order?: Record<string, string[]>,
): U[] => {
  const seen = new Set<string>();
  const ordered: U[] = [];
  for (const { purpose } of noticePurposeRows(products, purposes, order)) {
    if (!seen.has(purpose.uuid)) {
      seen.add(purpose.uuid);
      ordered.push(purpose);
    }
  }
  return ordered;
};
